import { db } from "../../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import { buildCanonicalConversionDataset, getBookingReferenceSeconds, getBookingGroupSize } from "../../../utils/analytics/conversion-metrics"
import { GROUP_SIZE_BUCKETS, UNKNOWN_BUCKET, bucketKeyForSize } from "./buckets"

// Firestore "in" queries accept at most 10 values (v8 SDK).
const CLIENT_CHUNK_SIZE = 10

// How far back the missed-business margin baseline reaches.
export const MARGIN_BASELINE_MONTHS = 6

const chunkArray = (array, size) => {
	const chunks = []
	for (let i = 0; i < array.length; i += size) {
		chunks.push(array.slice(i, i + size))
	}
	return chunks
}

export const gbp = new Intl.NumberFormat("en-GB", {
	style: "currency",
	currency: "GBP",
	maximumFractionDigits: 0,
})

// The active sales agents on the leaderboard (same definition the Conversions,
// Target Tracker and Team Analytics pages use).
export const fetchActiveAgents = async () => {
	const snapshot = await db.collection("users").where("show_for.targets", "==", true).orderBy("first_name").get()

	return snapshot.docs.map((doc) => ({
		id: doc.id,
		name: `${doc.data().first_name} ${doc.data().last_name}`.trim(),
	}))
}

// Canonical conversion dataset for a date window, identical de-duplication to
// the Conversions page. Returns { finalEnquiries, allBookings, ... } with a
// group_size on every enquiry and booking.
export const fetchPeriodDataset = async (startMs, endMs) => {
	const startTs = firebase.firestore.Timestamp.fromMillis(startMs)
	const endTs = firebase.firestore.Timestamp.fromMillis(endMs)

	const [openEnquiries, removedEnquiries, enquiryBookings, directBookings] = await Promise.all([
		db.collection("enquiries").where("created", ">=", startTs).where("created", "<=", endTs).get(),
		db.collection("enquiries_removed").where("enquiry.created", ">=", startTs).where("enquiry.created", "<=", endTs).get(),
		db.collection("bookings").where("enquiry.created", ">=", startTs).where("enquiry.created", "<=", endTs).get(),
		db.collection("bookings").where("created", ">=", startTs).where("created", "<=", endTs).get(),
	])

	return buildCanonicalConversionDataset({
		openEnquiryDocs: openEnquiries.docs,
		removedEnquiryDocs: removedEnquiries.docs,
		enquiryBookingDocs: enquiryBookings.docs,
		directBookingDocs: directBookings.docs,
	})
}

// Earliest booking timestamp (seconds) per client, used to classify enquiries
// and bookings as new vs repeat business. Chunked "in" queries keep this to the
// clients we actually care about rather than scanning every booking.
export const buildHistoryMapForClients = async (clientIds) => {
	const uniqueClientIds = [...new Set((clientIds || []).filter(Boolean))]
	if (!uniqueClientIds.length) return new Map()

	const historyMap = new Map()
	const chunks = chunkArray(uniqueClientIds, CLIENT_CHUNK_SIZE)
	const snapshots = await Promise.all(chunks.map((chunk) => db.collection("bookings").where("client", "in", chunk).get()))

	snapshots.forEach((snapshot) => {
		snapshot.docs.forEach((doc) => {
			const data = doc.data()
			const clientId = data.client
			const createdSeconds = getBookingReferenceSeconds(data)
			if (!clientId || !createdSeconds) return

			if (!historyMap.has(clientId) || historyMap.get(clientId) > createdSeconds) {
				historyMap.set(clientId, createdSeconds)
			}
		})
	})

	return historyMap
}

// Per-bucket margin baseline from the trailing six months of bookings. Mirrors
// the margin-per-booking model the pipeline forecast uses, but windowed to six
// months and segmented by group size. Returns a map keyed by bucket key with
// { count, totalProfit, avgMarginPerBooking, avgGroupSize, avgMarginPerPerson }.
export const fetchMarginBaseline = async (nowMs) => {
	const startMs = moment(nowMs).subtract(MARGIN_BASELINE_MONTHS, "months").valueOf()
	const startTs = firebase.firestore.Timestamp.fromMillis(startMs)
	const snapshot = await db.collection("bookings").where("created", ">=", startTs).get()

	const baseline = {}
	;[...GROUP_SIZE_BUCKETS, UNKNOWN_BUCKET].forEach((bucket) => {
		baseline[bucket.key] = { count: 0, totalProfit: 0, totalGroupSize: 0 }
	})

	snapshot.docs.forEach((doc) => {
		const data = doc.data()
		const profit = data.margins?.profit
		if (profit == null || !Number.isFinite(Number(profit))) return

		const groupSize = getBookingGroupSize(data)
		const key = bucketKeyForSize(groupSize)
		baseline[key].count++
		baseline[key].totalProfit += Number(profit)
		baseline[key].totalGroupSize += groupSize
	})

	Object.values(baseline).forEach((entry) => {
		entry.avgMarginPerBooking = entry.count > 0 ? entry.totalProfit / entry.count : 0
		entry.avgGroupSize = entry.count > 0 ? entry.totalGroupSize / entry.count : 0
		entry.avgMarginPerPerson = entry.totalGroupSize > 0 ? entry.totalProfit / entry.totalGroupSize : 0
	})

	return { windowStartMs: startMs, windowEndMs: nowMs, buckets: baseline }
}

// Split records (enquiries or bookings) into new vs repeat business using the
// client-history map. A record is "repeat" when the client had an earlier
// booking before this record was created. Same rule as buildNewRepeatBreakdown.
export const splitNewRepeat = (records = [], historyMap = new Map()) => {
	let newCount = 0
	let repeatCount = 0

	records.forEach((record) => {
		const clientHistory = historyMap.get(record.client)
		const recordSeconds = Math.floor(record.created / 1000)
		const isRepeat = clientHistory && clientHistory < recordSeconds
		if (isRepeat) repeatCount++
		else newCount++
	})

	return { newCount, repeatCount }
}

// Conversion rate as a percentage number (or null when there's nothing to divide).
export const rate = (numerator, denominator) => {
	if (!denominator || denominator <= 0) return null
	return (numerator / denominator) * 100
}

// Consistent "12.34%" / "N/A" rendering for a rate produced by rate().
export const formatRate = (value) => {
	if (value == null) return "N/A"
	return `${value.toFixed(2)}%`
}
